import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import {
  Cpu,
  Shield,
  Zap,
  Radio,
  Key,
  Copy,
  Terminal,
  Database,
  CheckCircle2,
  RefreshCw,
  Play,
  Layers,
  Sparkles,
  Sliders
} from 'lucide-react';
import './LlmGateway.css';

const PRESET_PROMPTS = {
  financial: "Customer Alice Smith (email: alice.smith@enterprise.com, phone: 555-867-5309) reported an unauthorized charge on card 4532-1234-5678-9014. Her SSN is 123-45-6789. Please draft an urgent account verification and dispute response.",
  healthcare: "Patient intake report: John Doe (email: j.doe@patientcare.org, phone: 212-555-0199) admitted with symptoms. Primary SSN on file is 987-65-4321, residential IP logged as 192.168.1.105. Recommend treatment and specialist consult schedule.",
  hr: "Employee onboarding memo: Prepare profile for Elena Rostova (contact: elena.rostova@globalcorp.io, mobile: +1 415 555 2671). Payment method registered with corporate card 5105-1051-0510-5100. Generate welcome packet."
};

const LlmGateway = () => {
  const { token, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('streaming-proxy'); // 'streaming-proxy' | 'direct-pipeline'
  
  // Shared Configuration
  const [mode, setMode] = useState('TOKENIZE'); // TOKENIZE, FPE, REDACT
  const [apiKey, setApiKey] = useState('');
  const [schemas, setSchemas] = useState([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState('');
  const [copied, setCopied] = useState(false);

  // Direct Pipeline States
  const [prompt, setPrompt] = useState(PRESET_PROMPTS.financial);
  const [sessionId, setSessionId] = useState('');
  const [sanitizedResult, setSanitizedResult] = useState(null);
  const [llmCompletion, setLlmCompletion] = useState('');
  const [restoredResult, setRestoredResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Streaming Reverse Proxy States
  const [proxyModel, setProxyModel] = useState('gpt-4o');
  const [upstreamKey, setUpstreamKey] = useState('');
  const [streamEnabled, setStreamEnabled] = useState(true);
  const [proxyPrompt, setProxyPrompt] = useState(PRESET_PROMPTS.financial);
  const [proxyRunning, setProxyRunning] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const [streamChunks, setStreamChunks] = useState([]);
  const [proxyTelemetry, setProxyTelemetry] = useState(null);
  const [activeSnippetLang, setActiveSnippetLang] = useState('python');
  const [showRawFeed, setShowRawFeed] = useState(false);

  useEffect(() => {
    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else if (token) {
      fetch(`${API_BASE}/dashboard/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(keys => {
        if (Array.isArray(keys) && keys.length > 0) {
          const activeKey = keys.find(k => k.enabled && !k.revoked) || keys[0];
          const val = activeKey.apiKeyPreview || activeKey.apiKey || activeKey.maskedKey;
          if (val) setApiKey(val);
        }
      })
      .catch(() => {});
    }

    // Load custom schemas
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (savedApiKey) headers['X-API-KEY'] = savedApiKey;
    if (tenant?.uid) headers['X-TENANT-ID'] = tenant.uid;

    fetch(`${API_BASE}/admin/schemas?limit=50`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          setSchemas(data);
        }
      })
      .catch(() => {});
  }, [token, tenant?.uid]);

  const getHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (apiKey) headers['X-API-KEY'] = apiKey;
    if (tenant?.uid) headers['X-TENANT-ID'] = tenant.uid;
    return headers;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // -------------------------------------------------------------
  // Drop-In Streaming GenAI Reverse Proxy Execution
  // -------------------------------------------------------------
  const handleRunReverseProxy = async () => {
    setProxyRunning(true);
    setStreamedContent('');
    setStreamChunks([]);
    setProxyTelemetry(null);

    // Save flag for Onboarding Step 5 verification
    localStorage.setItem('vaultize_gateway_tested', 'true');

    const startTime = Date.now();
    const headers = getHeaders();

    if (upstreamKey.trim()) {
      headers['X-Upstream-Key'] = upstreamKey.trim();
    }
    headers['X-Vaultize-Mode'] = mode;
    if (selectedSchemaId) {
      headers['X-Vaultize-Schema-Id'] = selectedSchemaId;
    }

    const payload = {
      model: proxyModel,
      messages: [
        { role: 'user', content: proxyPrompt }
      ],
      stream: streamEnabled,
      vaultize_mode: mode,
      schema_id: selectedSchemaId || undefined
    };

    try {
      if (streamEnabled) {
        const response = await fetch(`${API_BASE}/gateway/llm/v1/chat/completions`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errText || 'Streaming request failed'}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let accumulated = '';
        let chunkCount = 0;
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // save incomplete line

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data:')) continue;

            const dataStr = trimmed.substring(5).trim();
            if (dataStr === '[DONE]') {
              break;
            }

            try {
              const chunkJson = JSON.parse(dataStr);
              chunkCount++;
              setStreamChunks(prev => [...prev.slice(-40), dataStr]);

              const delta = chunkJson.choices?.[0]?.delta;
              if (delta && delta.content) {
                accumulated += delta.content;
                setStreamedContent(accumulated);
              }
            } catch (e) {
              // raw text chunk fallback
            }
          }
        }

        const latency = Date.now() - startTime;
        setProxyTelemetry({
          latencyMs: latency,
          chunkCount,
          model: proxyModel,
          mode,
          status: 'COMPLETED_STREAM',
          charCount: accumulated.length
        });
      } else {
        // Standard non-streaming JSON
        const response = await fetch(`${API_BASE}/gateway/llm/v1/chat/completions`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errText || 'Request failed'}`);
        }

        const data = await response.json();
        const content = data.choices?.[0]?.message?.content || '';
        setStreamedContent(content);
        const latency = Date.now() - startTime;

        setProxyTelemetry({
          latencyMs: latency,
          chunkCount: 1,
          model: proxyModel,
          mode,
          status: 'COMPLETED_JSON',
          metadata: data.vaultize_metadata,
          usage: data.usage,
          charCount: content.length
        });
      }
    } catch (err) {
      alert(`Reverse Proxy Error: ${err.message}`);
    } finally {
      setProxyRunning(false);
    }
  };

  // -------------------------------------------------------------
  // Direct Pipeline Execution
  // -------------------------------------------------------------
  const handleSanitize = async () => {
    setLoading(true);
    setRestoredResult(null);
    localStorage.setItem('vaultize_gateway_tested', 'true');

    try {
      const res = await fetch(`${API_BASE}/gateway/llm/sanitize`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          prompt,
          sessionId: sessionId || undefined,
          mode,
          schemaId: selectedSchemaId || undefined
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to sanitize prompt`);
      const data = await res.json();
      setSanitizedResult(data);
      setSessionId(data.sessionId);

      // Auto-populate simulated completion
      if (data.entitiesDetected && data.entitiesDetected.length > 0) {
        const tokensUsed = data.entitiesDetected.map(e => e.token).join(', ');
        setLlmCompletion(`Dispute confirmed. A verification link has been sent to ${data.entitiesDetected[0].token}. Acknowledged entities for audit: [${tokensUsed}]. Status: PENDING_APPROVAL.`);
      } else {
        setLlmCompletion("No sensitive entities detected. Query processed safely.");
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!sessionId) {
      alert("No active session ID. Please sanitize a prompt first.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/gateway/llm/restore`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          sessionId,
          completion: llmCompletion
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to restore completion`);
      const data = await res.json();
      setRestoredResult(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFullSimulation = async () => {
    setLoading(true);
    localStorage.setItem('vaultize_gateway_tested', 'true');
    try {
      const res = await fetch(`${API_BASE}/gateway/llm/simulate`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          prompt,
          mode,
          schemaId: selectedSchemaId || undefined
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: Failed to simulate roundtrip`);
      const data = await res.json();
      setSessionId(data.session_id);
      setSanitizedResult({
        sessionId: data.session_id,
        sanitizedPrompt: data.sanitized_prompt,
        entitiesDetected: data.entities_detected,
        entityCount: data.entities_detected?.length || 0
      });
      setLlmCompletion(data.llm_raw_completion);
      setRestoredResult({
        sessionId: data.session_id,
        restoredCompletion: data.final_restored_output,
        restoredCount: data.entities_detected?.length || 0
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Code Snippets for Drop-In Reverse Proxy
  const proxySnippets = {
    python: `from openai import OpenAI

# 1. Point standard OpenAI SDK directly to Vaultize Reverse Proxy
client = OpenAI(
    base_url="${API_BASE}/gateway/llm/v1",
    api_key="${apiKey || 'your_vaultize_api_key'}"  # Dual-Auth Vaultize Key
)

# 2. Issue standard chat completions request with streaming
response = client.chat.completions.create(
    model="${proxyModel}",
    messages=[
        {"role": "user", "content": "Customer John Doe (SSN: 123-45-6789, email: john@med.org)"}
    ],
    stream=True,
    extra_headers={
        "X-Vaultize-Mode": "${mode}",
        ${selectedSchemaId ? `"X-Vaultize-Schema-Id": "${selectedSchemaId}",` : ''}
        ${upstreamKey ? `"X-Upstream-Key": "${upstreamKey}",` : '# "X-Upstream-Key": "sk-your-upstream-key"'}
    }
)

# 3. Vaultize sanitizes prompt, executes LLM, and restores plaintext tokens in SSE stream!
for chunk in response:
    print(chunk.choices[0].delta.content or "", end="", flush=True)`,

    curl: `# Stream OpenAI completion through Vaultize Zero-Trust Reverse Proxy
curl -N -X POST "${API_BASE}/gateway/llm/v1/chat/completions" \\
  -H "X-API-KEY: ${apiKey || 'your_vaultize_api_key'}" \\
  -H "X-TENANT-ID: ${tenant?.uid || 'your_tenant_uid'}" \\
  -H "X-Vaultize-Mode: ${mode}" \\
  ${selectedSchemaId ? `-H "X-Vaultize-Schema-Id: ${selectedSchemaId}" \\` : ''}
  ${upstreamKey ? `-H "X-Upstream-Key: ${upstreamKey}" \\` : ''}
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${proxyModel}",
    "messages": [
      {"role": "user", "content": "Doctor Jane (email: jane@hospital.org) diagnosed patient with SSN 123-45-6789."}
    ],
    "stream": ${streamEnabled}
  }'`,

    node: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "${API_BASE}/gateway/llm/v1",
  apiKey: "${apiKey || 'your_vaultize_api_key'}",
  defaultHeaders: {
    "X-Vaultize-Mode": "${mode}",
    ${selectedSchemaId ? `"X-Vaultize-Schema-Id": "${selectedSchemaId}",` : ''}
  }
});

const stream = await client.chat.completions.create({
  model: "${proxyModel}",
  messages: [{ role: "user", content: "Analyze customer account" }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content || "");
}`
  };

  return (
    <div className="llm-gateway-page">
      <header className="llm-gateway-header">
        <div className="llm-header-badge">
          <span className="badge-pulse"></span>
          <span>ENTERPRISE ZERO-TRUST GENAI GATEWAY • SPRINT 4</span>
        </div>
        <h1>Zero-Trust LLM Privacy &amp; Streaming Reverse Proxy</h1>
        <p>
          Drop-in OpenAI &amp; Anthropic privacy gateway. Scrub PII, card numbers, and health records at inference time,
          route through your model of choice with real-time SSE chunked streaming, and transparently restore cleartext values.
        </p>
      </header>

      {/* Gateway Mode Switcher */}
      <div className="gateway-view-tabs">
        <button
          className={`gateway-tab-btn ${activeTab === 'streaming-proxy' ? 'active' : ''}`}
          onClick={() => setActiveTab('streaming-proxy')}
        >
          <Radio size={16} />
          <span>1. Drop-in OpenAI Streaming Reverse Proxy</span>
          <span className="tab-pill-highlight">NEW</span>
        </button>
        <button
          className={`gateway-tab-btn ${activeTab === 'direct-pipeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('direct-pipeline')}
        >
          <Sliders size={16} />
          <span>2. Direct Prompt Sanitization &amp; Restoration Pipeline</span>
        </button>
      </div>

      {/* Global Configuration Strip (Mode & Schema Linking) */}
      <div className="llm-control-bar">
        <div className="control-group">
          <span className="control-label">Masking Strategy:</span>
          <select value={mode} onChange={(e) => setMode(e.target.value)} className="mode-select">
            <option value="TOKENIZE">Session Quarantine Tokens (e.g. [EMAIL_1])</option>
            <option value="FPE">Format-Preserving Encryption (NIST FF3-1)</option>
            <option value="REDACT">Permanent Redaction ([REDACTED])</option>
          </select>
        </div>

        <div className="control-group schema-link-group">
          <span className="control-label">
            <Database size={13} /> Active Schema Link:
          </span>
          <select
            value={selectedSchemaId}
            onChange={(e) => setSelectedSchemaId(e.target.value)}
            className="schema-select"
          >
            <option value="">Default Built-in PII (Cards, SSN, Emails, Phones, IPs)</option>
            {schemas.map((s) => (
              <option key={s.id} value={s.id}>
                Schema: {s.name} ({s.id.substring(0, 8)}...)
              </option>
            ))}
          </select>
          {selectedSchemaId && (
            <span className="schema-active-badge">✓ Custom Regex Linked</span>
          )}
        </div>

        <div className="control-group preset-btn-group">
          <span className="control-label">Presets:</span>
          <button
            className="preset-btn"
            onClick={() => {
              setProxyPrompt(PRESET_PROMPTS.financial);
              setPrompt(PRESET_PROMPTS.financial);
            }}
          >
            💳 Financial Card
          </button>
          <button
            className="preset-btn"
            onClick={() => {
              setProxyPrompt(PRESET_PROMPTS.healthcare);
              setPrompt(PRESET_PROMPTS.healthcare);
            }}
          >
            🏥 HIPAA PHI
          </button>
          <button
            className="preset-btn"
            onClick={() => {
              setProxyPrompt(PRESET_PROMPTS.hr);
              setPrompt(PRESET_PROMPTS.hr);
            }}
          >
            💼 Employee KYC
          </button>
        </div>
      </div>

      {/* VIEW 1: DROP-IN STREAMING REVERSE PROXY */}
      {activeTab === 'streaming-proxy' && (
        <section className="streaming-proxy-view">
          <div className="proxy-grid">
            {/* Left: Proxy Request Configuration */}
            <div className="gateway-card proxy-config-card">
              <div className="card-header">
                <div className="step-indicator">PROXY INBOUND</div>
                <div>
                  <h3>OpenAI Chat Completions Endpoint</h3>
                  <span className="step-desc">POST /gateway/llm/v1/chat/completions</span>
                </div>
              </div>

              {/* Model & Upstream Configuration */}
              <div className="proxy-inputs-row">
                <div className="form-item flex-1">
                  <label className="input-label">Target Model:</label>
                  <select
                    value={proxyModel}
                    onChange={(e) => setProxyModel(e.target.value)}
                    className="mode-select full-width"
                  >
                    <option value="gpt-4o">gpt-4o (OpenAI Flagship)</option>
                    <option value="gpt-4o-mini">gpt-4o-mini (Low-latency)</option>
                    <option value="claude-3-5-sonnet">claude-3-5-sonnet (Anthropic)</option>
                    <option value="gemini-1.5-pro">gemini-1.5-pro (Google DeepMind)</option>
                  </select>
                </div>

                <div className="form-item stream-toggle-item">
                  <label className="input-label">Streaming (SSE):</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={streamEnabled}
                      onChange={(e) => setStreamEnabled(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">{streamEnabled ? 'stream: true (SSE)' : 'stream: false (JSON)'}</span>
                  </label>
                </div>
              </div>

              <div className="form-item">
                <div className="label-with-hint">
                  <label className="input-label">Upstream AI Key (Optional):</label>
                  <span className="field-hint">Leave blank for Zero-Trust Local Enclave simulator</span>
                </div>
                <input
                  type="password"
                  className="upstream-key-input"
                  placeholder="sk-proj-xxxx... (Passed in X-Upstream-Key)"
                  value={upstreamKey}
                  onChange={(e) => setUpstreamKey(e.target.value)}
                />
              </div>

              <div className="form-item">
                <label className="input-label">Prompt Content (messages[0].content):</label>
                <textarea
                  className="prompt-textarea"
                  rows="5"
                  value={proxyPrompt}
                  onChange={(e) => setProxyPrompt(e.target.value)}
                  placeholder="Enter sensitive prompt to sanitize and stream..."
                />
              </div>

              <div className="proxy-action-row">
                <button
                  className="primary-btn run-proxy-btn"
                  onClick={handleRunReverseProxy}
                  disabled={proxyRunning || !proxyPrompt.trim()}
                >
                  {proxyRunning ? (
                    <>
                      <RefreshCw size={15} className="spin-animation" />
                      <span>Streaming Tokens from Gateway...</span>
                    </>
                  ) : (
                    <>
                      <Play size={15} />
                      <span>Execute Reverse Proxy Request</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Right: Live Stream Output & Telemetry */}
            <div className="gateway-card proxy-output-card">
              <div className="card-header">
                <div className="step-indicator step-2">PROXY OUTBOUND</div>
                <div className="output-header-right">
                  <div>
                    <h3>Live Reconstituted Completion Stream</h3>
                    <span className="step-desc">Real-time Server-Sent Events with client plaintext restoration</span>
                  </div>
                  {proxyRunning && (
                    <span className="live-streaming-indicator">
                      <span className="pulse-dot"></span> LIVE SSE STREAMING
                    </span>
                  )}
                </div>
              </div>

              {/* Telemetry Strip */}
              {proxyTelemetry && (
                <div className="proxy-telemetry-strip">
                  <div className="tele-item">
                    <span className="tele-label">Total Latency:</span>
                    <span className="tele-val highlight">{proxyTelemetry.latencyMs}ms</span>
                  </div>
                  <div className="tele-item">
                    <span className="tele-label">Delivery Mode:</span>
                    <span className="tele-val font-mono">{proxyTelemetry.status}</span>
                  </div>
                  <div className="tele-item">
                    <span className="tele-label">Stream Chunks:</span>
                    <span className="tele-val">{proxyTelemetry.chunkCount}</span>
                  </div>
                  <div className="tele-item">
                    <span className="tele-label">Compliance:</span>
                    <span className="tele-val text-emerald">100% Descoping</span>
                  </div>
                </div>
              )}

              {/* Terminal Typewriter Box */}
              <div className="terminal-screen-wrapper">
                <div className="terminal-header-bar">
                  <span className="terminal-dot red"></span>
                  <span className="terminal-dot yellow"></span>
                  <span className="terminal-dot green"></span>
                  <span className="terminal-title">OpenAI Client • EventStream Receiver</span>
                  <button
                    className="terminal-copy-btn"
                    onClick={() => copyToClipboard(streamedContent)}
                    disabled={!streamedContent}
                  >
                    {copied ? '✓ Copied' : 'Copy Output'}
                  </button>
                </div>

                <div className="terminal-body">
                  {streamedContent ? (
                    <pre className="terminal-text-content">
                      {streamedContent}
                      {proxyRunning && <span className="typewriter-cursor">▋</span>}
                    </pre>
                  ) : (
                    <div className="terminal-empty-placeholder">
                      <Terminal size={32} />
                      <p>Click <strong>"Execute Reverse Proxy Request"</strong> to initiate live Server-Sent Events streaming with real-time token reconstitution.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Raw SSE Event Chunks Feed Toggle */}
              {streamChunks.length > 0 && (
                <div className="raw-feed-container">
                  <button
                    className="toggle-feed-btn"
                    onClick={() => setShowRawFeed(!showRawFeed)}
                  >
                    <span>{showRawFeed ? 'Hide Raw SSE Chunks Feed' : `Inspect Raw SSE Event Stream (${streamChunks.length} chunks)`}</span>
                  </button>

                  {showRawFeed && (
                    <div className="raw-chunks-feed">
                      {streamChunks.map((c, i) => (
                        <div key={i} className="sse-chunk-line">
                          <span className="chunk-num">{i + 1}</span>
                          <span className="chunk-data">data: {c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Integration Guide for Reverse Proxy */}
          <div className="integration-guide-card">
            <div className="guide-header">
              <div>
                <h3>Drop-in SDK Integration Guide</h3>
                <p>Swap your OpenAI base URL to Vaultize to protect production inference without rewriting prompt logic:</p>
              </div>
              <div className="lang-selector-pills">
                <button
                  className={`lang-pill ${activeSnippetLang === 'python' ? 'active' : ''}`}
                  onClick={() => setActiveSnippetLang('python')}
                >
                  Python (OpenAI SDK)
                </button>
                <button
                  className={`lang-pill ${activeSnippetLang === 'curl' ? 'active' : ''}`}
                  onClick={() => setActiveSnippetLang('curl')}
                >
                  cURL (Streaming)
                </button>
                <button
                  className={`lang-pill ${activeSnippetLang === 'node' ? 'active' : ''}`}
                  onClick={() => setActiveSnippetLang('node')}
                >
                  Node.js / TypeScript
                </button>
              </div>
            </div>

            <div className="code-carousel">
              <pre className="code-block curl-block">
                <code>{proxySnippets[activeSnippetLang]}</code>
              </pre>
            </div>
          </div>
        </section>
      )}

      {/* VIEW 2: DIRECT PROMPT PIPELINE */}
      {activeTab === 'direct-pipeline' && (
        <section className="direct-pipeline-view">
          <div className="llm-gateway-grid">
            {/* Left Column: Outbound Pipeline (Prompt Sanitization) */}
            <div className="gateway-card outbound-card">
              <div className="card-header">
                <div className="step-indicator">STEP 1</div>
                <div>
                  <h3>Inbound Prompt Sanitization</h3>
                  <span className="step-desc">Extracts PII &amp; replaces with ephemeral quarantine tokens</span>
                </div>
              </div>

              <div className="prompt-input-group">
                <label className="input-label">Raw Prompt (Sent from Your App / User):</label>
                <textarea
                  className="prompt-textarea"
                  rows="5"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Paste prompt containing sensitive PII..."
                />
              </div>

              <div className="action-row">
                <button className="primary-btn" onClick={handleSanitize} disabled={loading || !prompt.trim()}>
                  {loading ? 'Processing...' : '🛡️ Sanitize Prompt for LLM'}
                </button>
                <button className="simulate-btn" onClick={handleFullSimulation} disabled={loading}>
                  ⚡ Run End-to-End Simulation
                </button>
              </div>

              {sanitizedResult && (
                <div className="result-section">
                  <div className="section-title-bar">
                    <h4>Quarantined Prompt (Safe for External AI Models):</h4>
                    <button
                      className="copy-btn"
                      onClick={() => copyToClipboard(sanitizedResult.sanitizedPrompt)}
                    >
                      {copied ? '✓ Copied' : '📋 Copy Prompt'}
                    </button>
                  </div>
                  <pre className="code-block sanitized-block">
                    {sanitizedResult.sanitizedPrompt}
                  </pre>

                  <div className="entities-detected-panel">
                    <div className="entities-header">
                      <span>🛡️ Detected Entities ({sanitizedResult.entityCount || 0})</span>
                      <span className="session-tag">Session: {sanitizedResult.sessionId?.substring(0, 8)}...</span>
                    </div>
                    <div className="entities-list">
                      {sanitizedResult.entitiesDetected?.map((entity, idx) => (
                        <div key={idx} className="entity-chip">
                          <span className={`entity-type-badge type-${entity.type.toLowerCase()}`}>
                            {entity.type}
                          </span>
                          <span className="entity-token">{entity.token}</span>
                          <span className="entity-masked">{entity.maskedSnippet}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Inbound Pipeline (Completion Restoration) */}
            <div className="gateway-card inbound-card">
              <div className="card-header">
                <div className="step-indicator step-2">STEP 2</div>
                <div>
                  <h3>LLM Completion Context Restoration</h3>
                  <span className="step-desc">Matches ephemeral session tokens &amp; restores original plaintext</span>
                </div>
              </div>

              <div className="prompt-input-group">
                <label className="input-label">LLM Completion (Received from AI Provider):</label>
                <textarea
                  className="prompt-textarea completion-textarea"
                  rows="5"
                  value={llmCompletion}
                  onChange={(e) => setLlmCompletion(e.target.value)}
                  placeholder="LLM response containing session tokens will appear here..."
                />
              </div>

              <div className="action-row">
                <button
                  className="secondary-btn restore-btn"
                  onClick={handleRestore}
                  disabled={loading || !llmCompletion.trim()}
                >
                  {loading ? 'Restoring...' : '🔓 Restore Plaintext PII in Completion'}
                </button>
              </div>

              {restoredResult && (
                <div className="result-section restored-section">
                  <div className="section-title-bar">
                    <h4>Final Reconstituted Output (Delivered to User):</h4>
                    <span className="restore-count-badge">
                      {restoredResult.restoredCount} entities restored
                    </span>
                  </div>
                  <pre className="code-block restored-block">
                    {restoredResult.restoredCompletion}
                  </pre>
                  <div className="compliance-banner">
                    <span className="banner-icon">✅</span>
                    <span>
                      <strong>Full Compliance Maintained:</strong> Zero raw PII crossed third-party AI network boundaries.
                      Audited under Tenant ID <code>{tenant?.uid || 'Default'}</code>.
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LlmGateway;
